import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Avatar,
  Alert,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  CheckCircle as CheckCircleIcon,
  Flag as FlagIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import config from '../../config';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { isTeacher } from '../../constants/roles';
import { EmptyState } from './primitives';
import { SEVERITY_CONFIG, STATUS_CONFIG, CATEGORY_LABELS, formatTimestamp } from './SafetyFlagsView';

// Queue-position lookup: prev_id/next_id are only computed by GET
// /api/safety/flags (list_flags), scoped to whatever filters+sort produced
// the current queue — there's no per-flag endpoint for "where does this one
// sit". The Review button in SafetyFlagsView always navigates here carrying
// the exact same query string the list was showing (see its onReview
// below), so re-issuing that same list request here (capped, not the whole
// table) is enough to recover this flag's neighbors — and works after a
// refresh/direct deep link too, as long as the URL's filters still match.
// Beyond the cap, Prev/Next just disable rather than lying about position.
const NEIGHBOR_SEARCH_PAGES = 5;
const NEIGHBOR_PAGE_SIZE = 100;

function buildFilterParams(search) {
  const qs = new URLSearchParams(search);
  const params = new URLSearchParams();
  ['status', 'severity', 'source', 'school', 'username'].forEach((key) => {
    const v = qs.get(key);
    if (v) params.set(key, v);
  });
  params.set('sort', qs.get('sort') || 'oldest');
  return params;
}

// Five outcomes, each with a one-line consequence — the approved
// prototype's exact wording, so an admin never has to guess what a status
// change actually does before committing to it. `requiresAdmin` is the one
// outcome (RESOLVED) a TEACHER can't set — see PATCH /api/safety/flags/{id}
// (app/routes/safety.py's _TEACHER_SETTABLE_STATUSES): a formal case-closed
// outcome is reserved for SCHOOL_ADMIN and above, not part of a teacher's
// day-to-day triage. Disabled here client-side (with an explanatory
// tooltip, not just hidden) so a teacher understands *why* rather than
// discovering it only via a 403 on Save.
const OUTCOME_OPTIONS = [
  { value: 'REVIEWED', label: 'Mark as reviewed', consequence: 'Closes the flag. Records your name and time.' },
  { value: 'ESCALATED', label: 'Escalate', consequence: 'Notifies the school admin and keeps it open.' },
  { value: 'DISMISSED', label: 'Dismiss', consequence: 'Closes it as a false alarm.' },
  { value: 'RESOLVED', label: 'Resolve', consequence: 'Closes with a recorded outcome.', requiresAdmin: true },
  { value: 'PENDING', label: 'Keep pending', consequence: 'Stays in the queue for another admin to decide.' },
];

export default function SafetyReviewPage({ currentUser, notify, onFlagsChanged }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { flagId } = useParams();

  const [flag, setFlag] = useState(null);
  const [flagLoading, setFlagLoading] = useState(true);
  const [flagError, setFlagError] = useState('');

  const [neighbors, setNeighbors] = useState({ prevId: null, nextId: null, resolved: false });

  const [messages, setMessages] = useState([]);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState('');

  // '' is a real, distinct value here — "no outcome chosen yet", not a
  // fifth OUTCOME_OPTIONS entry. A still-PENDING flag starts with nothing
  // selected (see fetchFlag below) so a careless Save can't silently
  // stamp a decision (`REVIEWED`) the admin never actually made; an
  // already-decided flag still preselects its current status.
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Whether the admin has actively touched the outcome radios this visit
  // (as opposed to it just reflecting whatever loaded). Gates both Save's
  // disabled state for a still-PENDING flag (D1) and whether `status` is
  // even included in the PATCH body (C5) — a notes-only edit on an
  // already-decided flag must not re-send its unchanged status and
  // re-stamp reviewed_by/reviewed_at.
  const [statusTouched, setStatusTouched] = useState(false);
  // Set once per fetchFlag response; never mutated afterward (in
  // particular, a successful Save below does NOT flip this back to
  // false), so it purely records "did this flag need a decision when it
  // was opened" for as long as this page instance is showing it.
  const flagWasPendingRef = useRef(false);

  const highlightRef = useRef(null);
  // Guards the {viewed:true} PATCH below to firing exactly once per flag —
  // re-renders (state updates from Save, neighbor prefetch landing, etc.)
  // must not re-send it. Reset implicitly: flagId changing means fetchFlag
  // (below) is a new callback identity, which re-runs the effect and finds
  // this ref no longer matching the new flagId.
  const viewedStampedForRef = useRef(null);

  const isMasterAdmin = currentUser?.role === 'MASTER_ADMIN' || currentUser?.role === 'admin';
  const isTeacherRole = isTeacher(currentUser?.role);

  const backToList = () => navigate({ pathname: '/admin/safety', search: location.search });

  // Stamps {viewed: true} on this flag exactly once per flagId — idempotent
  // server-side (first viewer wins), but there's no reason to re-send it on
  // every re-render. Fired after the flag itself has loaded (see fetchFlag
  // below), not before, so there's a real flag on screen to attach the
  // stamp to. Best-effort: a failure here shouldn't block review, so it's
  // swallowed rather than surfaced as an error banner.
  const stampViewed = useCallback(() => {
    if (viewedStampedForRef.current === flagId) return;
    viewedStampedForRef.current = flagId;
    authFetch(`${config.API_BASE_URL}/api/safety/flags/${flagId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ viewed: true }),
    })
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (!data) return; // 401 — already redirected
        // Merge only the viewed stamp, not the whole record — the GET that
        // triggered this may already be stale relative to this response in
        // theory, but the fields being merged (viewed_at/viewed_by) never
        // change again once set (idempotent, first-viewer-wins), so this
        // can never clobber anything newer.
        setFlag((prev) => (prev ? { ...prev, viewed_at: data.viewed_at, viewed_by: data.viewed_by } : data));
      })
      .catch(() => {
        // Silent — viewing a flag isn't itself a review decision, so a
        // failed stamp attempt doesn't need to interrupt the admin.
      });
  }, [flagId, navigate]);

  // ── Flag detail ───────────────────────────────────────────────────────
  const fetchFlag = useCallback(
    (signal) => {
      setFlagLoading(true);
      setFlagError('');
      authFetch(`${config.API_BASE_URL}/api/safety/flags/${flagId}`, {}, { signal })
        .then((res) => handleApiResponse(res, navigate))
        .then((data) => {
          if (data === null || signal?.aborted) return; // 401 — already redirected
          setFlag(data);
          flagWasPendingRef.current = data.status === 'PENDING';
          setStatus(data.status === 'PENDING' ? '' : data.status);
          setStatusTouched(false);
          setNotes(data.review_notes || '');
          stampViewed();
        })
        .catch((err) => {
          if (err?.name === 'AbortError' || signal?.aborted) return;
          setFlagError(describeAdminError(err, "Couldn't load this flag."));
        })
        .finally(() => {
          if (!signal?.aborted) setFlagLoading(false);
        });
    },
    [flagId, navigate, stampViewed]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchFlag(controller.signal);
    return () => controller.abort();
  }, [fetchFlag]);

  // ── Neighbor (prev/next) resolution ──────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setNeighbors({ prevId: null, nextId: null, resolved: false });

    (async () => {
      const filterParams = buildFilterParams(location.search);
      for (let page = 1; page <= NEIGHBOR_SEARCH_PAGES; page += 1) {
        const params = new URLSearchParams(filterParams);
        params.set('page', String(page));
        params.set('page_size', String(NEIGHBOR_PAGE_SIZE));
        try {
          // eslint-disable-next-line no-await-in-loop -- deliberately sequential: stop as soon as the flag is found rather than firing every page at once
          const res = await authFetch(`${config.API_BASE_URL}/api/safety/flags?${params.toString()}`, {}, { signal: controller.signal });
          // eslint-disable-next-line no-await-in-loop
          const data = await handleApiResponse(res, navigate);
          if (cancelled || controller.signal.aborted || data === null) return;
          const match = (data.flags || []).find((f) => String(f.id) === flagId);
          if (match) {
            setNeighbors({ prevId: match.prev_id, nextId: match.next_id, resolved: true });
            return;
          }
          if ((data.flags || []).length < NEIGHBOR_PAGE_SIZE) break; // no more pages
        } catch {
          break;
        }
      }
      if (!cancelled) setNeighbors({ prevId: null, nextId: null, resolved: true });
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [flagId, location.search, navigate]);

  // ── Conversation context ─────────────────────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    setContextLoading(true);
    setContextError('');
    authFetch(`${config.API_BASE_URL}/api/safety/flags/${flagId}/context?before=10&after=10`, {}, { signal: controller.signal })
      .then((res) => handleApiResponse(res, navigate))
      .then((data) => {
        if (data === null || controller.signal.aborted) return; // 401 — already redirected
        setMessages(data.messages || []);
      })
      .catch((err) => {
        if (err?.name === 'AbortError' || controller.signal.aborted) return;
        setContextError(describeAdminError(err, "Couldn't load the conversation."));
      })
      .finally(() => {
        if (!controller.signal.aborted) setContextLoading(false);
      });
    return () => controller.abort();
  }, [flagId, navigate]);

  // Scroll to and highlight the flagged message once the context loads.
  useEffect(() => {
    if (contextLoading || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [contextLoading, messages]);

  // A still-PENDING flag needs an explicit outcome before Save does
  // anything — see the `status`/`statusTouched` comment above. An
  // already-decided flag has no such requirement (its current decision is
  // already the preselected value), so this is only ever true pre-decision.
  const saveNeedsOutcome = flagWasPendingRef.current && !statusTouched;

  const handleSave = async () => {
    if (saveNeedsOutcome) return; // belt-and-braces; Save is disabled for this case
    // Defensive, belt-and-braces check — the Resolve radio is already
    // disabled (with a tooltip) for a TEACHER, so this should be
    // unreachable via the UI. Guards only against the option somehow being
    // submitted anyway (e.g. a stale `status` from before a role change)
    // rather than relying solely on the server's 403.
    if (status === 'RESOLVED' && isTeacherRole) {
      notify?.('Resolving a flag requires a school admin or above.', 'error');
      return;
    }
    setSaving(true);
    try {
      // `status` is only included when the admin actually changed the
      // outcome this visit. The backend stamps reviewed_by/reviewed_at on
      // *any* call that carries `status` (even re-sending the same value —
      // see app/routes/safety.py's PATCH handler), so a notes-only edit on
      // an already-decided flag must omit it entirely rather than resend
      // the unchanged value and re-stamp a decision nobody just made.
      const body = { review_notes: notes };
      if (statusTouched) body.status = status;
      const res = await authFetch(`${config.API_BASE_URL}/api/safety/flags/${flagId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      setFlag(data);
      // Reflect whatever the server actually recorded. If this admin didn't
      // touch the outcome, data.status can't have changed regardless; if
      // they did (including explicitly choosing "Keep pending"), show that
      // choice rather than reverting to the blank "undecided" state.
      setStatus(data.status === 'PENDING' && !statusTouched ? '' : data.status);
      notify?.('Flag updated.', 'success');
      onFlagsChanged?.();
    } catch (err) {
      notify?.(`Couldn't save this review. ${describeAdminError(err)}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const goToNeighbor = (id) => {
    if (!id) return;
    navigate({ pathname: `/admin/safety/${id}`, search: location.search });
  };

  const categoryChips = useMemo(() => flag?.categories || [], [flag]);
  const sev = flag ? SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.low : SEVERITY_CONFIG.low;
  const statusCfg = flag ? STATUS_CONFIG[flag.status] || { label: flag.status, color: 'default' } : null;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Breadcrumb — carries the exact query string it arrived with, so
          the list's tab/filters/sort are exactly as the admin left them. */}
      <Button
        onClick={backToList}
        startIcon={<ArrowBackIcon fontSize="small" />}
        sx={{ mb: 2, color: adminColors.inkSoft, fontWeight: 600, ...TOUCH_HIT_SX }}
      >
        Back to Safety Monitoring
      </Button>

      {flagError && <Alert severity="error" sx={{ mb: 2 }}>{flagError}</Alert>}

      {flagLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : flag ? (
        <>
          {/* Header: identity, severity/status, prev/next queue walk */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Avatar sx={{ bgcolor: adminColors.accent, width: 36, height: 36 }}>
                {(flag.username?.[0] || 'U').toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: adminColors.ink }} noWrap>
                  {flag.username}
                </Typography>
                {isMasterAdmin && flag.school && (
                  <Typography variant="caption" sx={{ color: adminColors.inkSoft }}>{flag.school}</Typography>
                )}
              </Box>
              <Chip label={sev.label} size="small" sx={{ bgcolor: sev.bg, color: sev.fg, fontWeight: 700, ml: 1 }} />
              <Chip label={statusCfg.label} size="small" variant="outlined" color={statusCfg.color} sx={statusCfg.sx} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title="Previous in queue">
                <span>
                  <IconButton
                    onClick={() => goToNeighbor(neighbors.prevId)}
                    disabled={!neighbors.resolved || !neighbors.prevId}
                    aria-label="Previous flag"
                    sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next in queue">
                <span>
                  <IconButton
                    onClick={() => goToNeighbor(neighbors.nextId)}
                    disabled={!neighbors.resolved || !neighbors.nextId}
                    aria-label="Next flag"
                    sx={{ ...TOUCH_HIT_SX, color: adminColors.inkSoft }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'flex-start' }}>
            {/* Conversation */}
            <Paper
              variant="outlined"
              sx={{ flex: 1, minWidth: 0, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)', maxHeight: { lg: 'calc(100vh - 220px)' }, overflowY: 'auto' }}
            >
              {contextError && (
                <Box p={2.5}>
                  <Alert severity="error">{contextError}</Alert>
                </Box>
              )}
              {contextLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                messages.map((msg) => {
                  const isHighlighted = msg.id === flag.message_id;
                  const isUser = msg.role === 'user';
                  return (
                    <Box
                      key={msg.id}
                      ref={isHighlighted ? highlightRef : undefined}
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid rgba(24,24,27,0.06)',
                        backgroundColor: isHighlighted ? '#fff3e0' : isUser ? adminColors.surfaceRaised : '#f3f6fb',
                        borderLeft: isHighlighted ? '4px solid #ff6d00' : '4px solid transparent',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: isUser ? adminColors.accent : '#43a047' }}>
                          {isUser ? (flag.username?.[0] || 'U').toUpperCase() : 'AI'}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ color: adminColors.inkSoft }}>
                          {isUser ? flag.username : 'Assistant'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: adminColors.inkSoft, ml: 'auto' }}>
                          {formatTimestamp(msg.timestamp)}
                        </Typography>
                        {isHighlighted && (
                          <Chip icon={<FlagIcon />} label="Flagged message" size="small" color="warning" sx={{ ml: 1 }} />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ pl: 4, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: adminColors.ink }}>
                        {msg.content}
                      </Typography>
                    </Box>
                  );
                })
              )}
              {!contextLoading && !contextError && messages.length === 0 && (
                <Typography variant="body2" sx={{ color: adminColors.inkSoft, textAlign: 'center', py: 4 }}>
                  No messages found.
                </Typography>
              )}
            </Paper>

            {/* Decision panel */}
            <Paper
              variant="outlined"
              sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0, borderRadius: radius.surface, borderColor: 'rgba(24,24,27,0.08)', p: 2.5, position: { lg: 'sticky' }, top: { lg: 16 } }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: adminColors.ink, mb: 1.5 }}>Review</Typography>

              {categoryChips.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                  {categoryChips.map((cat) => (
                    <Chip key={cat} label={CATEGORY_LABELS[cat] || cat} size="small" color="error" variant="outlined" />
                  ))}
                </Box>
              )}

              <Typography variant="caption" sx={{ color: adminColors.inkSoft, display: 'block', mb: flag.viewed_by ? 0.5 : 2 }}>
                Flagged {formatTimestamp(flag.created_at)}
                {flag.message?.source === 'agent_tutor' ? ' · Agent Tutor' : ''}
              </Typography>

              {/* First-viewer stamp — independent of any decision below.
                  Always the flag's own viewed_by/viewed_at (first viewer
                  wins server-side), not necessarily this admin, if someone
                  else opened it first. */}
              {flag.viewed_by && (
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: adminColors.inkSoft, mb: 2 }}>
                  <VisibilityIcon sx={{ fontSize: 13 }} />
                  Viewed by {flag.viewed_by} · {formatTimestamp(flag.viewed_at)}
                </Typography>
              )}

              <Typography
                id="review-outcome-label"
                variant="caption"
                sx={{ display: 'block', fontWeight: 700, color: adminColors.ink, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.68rem', mb: 1 }}
              >
                Outcome
              </Typography>

              <RadioGroup
                aria-labelledby="review-outcome-label"
                value={status}
                onChange={(e) => {
                  const value = e.target.value;
                  const opt = OUTCOME_OPTIONS.find((o) => o.value === value);
                  // Refuse the selection itself (RESOLVED, for a TEACHER) —
                  // but note this handler doesn't fire at all for a truly
                  // HTML-`disabled` input, which is exactly the bug D3
                  // fixes: that option is no longer `disabled`, only
                  // `aria-disabled`, specifically so Tab/arrow-key
                  // navigation can still land on it and this guard can
                  // still run instead of the option being unreachable.
                  if (opt?.requiresAdmin && isTeacherRole) return;
                  setStatus(value);
                  setStatusTouched(true);
                }}
                sx={{ mb: 2, gap: 0.75 }}
              >
                {OUTCOME_OPTIONS.map((opt) => {
                  const restricted = opt.requiresAdmin && isTeacherRole;
                  const selected = status === opt.value;
                  const option = (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      // Deliberately NOT `disabled` — see D3: the native
                      // `disabled` attribute removes an <input> from both
                      // Tab order and the radio group's native arrow-key
                      // roving, so a keyboard/screen-reader teacher would
                      // never even discover this option exists. Left fully
                      // focusable; `aria-disabled` announces its state to
                      // assistive tech, and the RadioGroup's onChange above
                      // refuses to act on it regardless of how it's
                      // "selected" (click, Space, or arrow-key roving).
                      control={
                        <Radio
                          size="small"
                          inputProps={restricted ? { 'aria-disabled': 'true' } : undefined}
                          sx={{ pt: 0.25, ...TOUCH_HIT_SX, '&.Mui-focusVisible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2, borderRadius: '50%' } }}
                        />
                      }
                      label={
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: restricted ? adminColors.inkSoft : adminColors.ink }}>
                            {opt.label}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, lineHeight: 1.4 }}>
                            {opt.consequence}
                          </Typography>
                        </Box>
                      }
                      sx={{
                        m: 0,
                        alignItems: 'flex-start',
                        borderRadius: radius.control,
                        border: '1px solid',
                        borderColor: selected ? adminColors.accent : 'rgba(24,24,27,0.1)',
                        backgroundColor: selected ? adminColors.accentLight : 'transparent',
                        px: 1.25,
                        py: 0.75,
                        opacity: restricted ? 0.55 : 1,
                        cursor: restricted ? 'not-allowed' : 'pointer',
                        transition: 'border-color 0.12s ease, background-color 0.12s ease',
                        '&:hover': restricted ? {} : { backgroundColor: selected ? adminColors.accentLight : adminColors.surface },
                        // D9: the focus ring on the Radio itself is a
                        // 20px dot easy to lose track of while arrowing
                        // through five visually similar cards — ring the
                        // whole card whenever its radio has keyboard focus.
                        '&:has(.Mui-focusVisible)': {
                          outline: `2px solid ${adminColors.accent}`,
                          outlineOffset: 2,
                        },
                      }}
                    />
                  );
                  return restricted ? (
                    <Tooltip
                      key={opt.value}
                      title="Resolving a flag requires a school admin or above. Teachers can mark flags reviewed, dismissed, or escalated."
                    >
                      <span>{option}</span>
                    </Tooltip>
                  ) : option;
                })}
              </RadioGroup>

              <TextField
                label="Review notes (optional)"
                multiline
                rows={4}
                fullWidth
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 1.5 }}
              />

              <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mb: 2 }}>
                Students are never notified of flag decisions.
              </Typography>

              {saveNeedsOutcome && (
                <Typography
                  id="save-needs-outcome-hint"
                  variant="caption"
                  sx={{ display: 'block', color: adminColors.warningAlertBg, fontWeight: 600, mb: 1, mt: -1 }}
                >
                  Choose an outcome to save.
                </Typography>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={handleSave}
                aria-describedby={saveNeedsOutcome ? 'save-needs-outcome-hint' : undefined}
                disabled={saving || saveNeedsOutcome}
                startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
                sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark }, ...TOUCH_HIT_SX }}
              >
                Save
              </Button>

              {flag.reviewed_by && (
                <Typography variant="caption" sx={{ display: 'block', color: adminColors.inkSoft, mt: 1.5 }}>
                  Last reviewed by {flag.reviewed_by}{flag.reviewed_at ? ` · ${formatTimestamp(flag.reviewed_at)}` : ''}
                </Typography>
              )}
            </Paper>
          </Box>
        </>
      ) : (
        !flagError && (
          <EmptyState
            icon={<FlagIcon />}
            title="Flag not found"
            description="It may have been deleted, or you may not have access to it."
            action={
              <Button variant="outlined" onClick={backToList} sx={TOUCH_HIT_SX}>
                Back to Safety Monitoring
              </Button>
            }
          />
        )
      )}
    </Box>
  );
}
